import {format} from 'date-fns';
import {combine, merge, sample} from 'effector';

import {showRestError} from '@app/errors';
import {rootDomain} from '@app/model';
import {DATE_REQUEST_PATTERN} from '@consts/date';
import {
    CounterpartiesPageResponse,
    CounterpartiesRequestParams,
    CounterpartyType,
} from '@entities/counterparties/types';
import {dateModel as date} from '@entities/date/model';
import {userModel} from '@entities/user/model';
import {createGridTableModel, initFiltersTable, initSortingsTable} from '@shared/composables/table';

import {mapOrders} from '@utils/grid';

import * as api from './api';

const domain = rootDomain.createDomain('InvoicesList');

const fetchDataFx = domain.createEffect({handler: api.fetch});
fetchDataFx.failData.watch(showRestError);

const grid = createGridTableModel({domain});

export const {
    filters$,
    filtersChanged,
    filtersReset,
    defaultFilters,
    sortings$,
    sortingsChanged,
    sortingsReset,
    defaultSortings,
    pageNumber$,
    pageNumberChanged,
    pageSize$,
    pageSizeChanged,
    Gate: CounterpartiesGate,
    tableStateChanged,
    stateReset,
} = grid;

initFiltersTable(filters$, filtersChanged, filtersReset, defaultFilters, stateReset);
initSortingsTable(sortings$, sortingsChanged, sortingsReset, defaultSortings, stateReset);

const data$ = domain.createStore<CounterpartiesPageResponse | null>(null);

const searchChanged = domain.createEvent<string>();
const search$ = domain.createStore('').on(searchChanged, (_, value) => value);

const typeChanged = domain.createEvent<CounterpartyType>();
const type$ = domain.createStore<CounterpartyType>('SELLERS').on(typeChanged, (_, value) => value);

export const gridTokensCache$ = domain.createStore<Record<number, string | null>>({0: null});
export const gridCacheChanged = domain.createEvent<{pageNumber: number; token: string | null}>();
export const gridCacheReset = domain.createEvent();

pageNumber$.reset(filtersChanged, sortingsChanged, stateReset, typeChanged);
pageSize$.reset(stateReset);

data$.on(fetchDataFx.doneData, (_, data) => data).reset(CounterpartiesGate.close);

gridTokensCache$
    .on(gridCacheReset, () => ({0: null}))
    .on(gridCacheChanged, (state, {pageNumber, token}) => ({
        ...state,
        [pageNumber]: token,
    }));

sample({
    clock: fetchDataFx.doneData,
    source: pageNumber$,
    fn: (pageNumber, result) => ({
        pageNumber: pageNumber + 1,
        token: result.nextPageRef ?? null,
    }),
    target: gridCacheChanged,
});

sample({
    clock: merge([
        filtersChanged,
        sortingsChanged,
        searchChanged,
        typeChanged,
        date.changed,
        stateReset,
    ]),
    target: gridCacheReset,
});

sample({
    clock: [CounterpartiesGate.state],
    filter: (newType, currentType) => currentType.type && newType !== currentType.type,
    source: type$,
    target: typeChanged,
});

sample({
    clock: merge([tableStateChanged, searchChanged, typeChanged, date.changed]),
    source: combine({
        taxNumber: userModel.state.map((s) => s.profile?.company.tin ?? ''),
        pageNumber: pageNumber$,
        pageSize: pageSize$,
        order: sortings$,
        date: date.state,
        searchTin: search$,
        role: type$,
        cache: gridTokensCache$,
    }),
    filter: CounterpartiesGate.status,
    fn: ({
        pageNumber,
        pageSize,
        order,
        searchTin,
        role,
        date,
        taxNumber,
        cache,
    }): CounterpartiesRequestParams => ({
        ...(taxNumber && {taxNumber}),
        role,
        paging: {pageSize, ...(cache?.[pageNumber] && {pageRef: cache[pageNumber]})},
        ...(date.range.from && {dateFrom: format(date.range.from, DATE_REQUEST_PATTERN)}),
        ...(date.range.to && {dateTo: format(date.range.to, DATE_REQUEST_PATTERN)}),
        ...(searchTin && {searchTin}),
        ...(Object.keys(order).length && {order: mapOrders(order)}),
    }),
    target: fetchDataFx,
});

const $counterpartiesState = combine({
    data: data$,
    type: type$,
    pageNumber: pageNumber$,
    pageSize: pageSize$,
    filters: filters$,
    sortings: sortings$,
    search: search$,
    pending: fetchDataFx.pending,
    gridTokensCache: gridTokensCache$,
});

export const counterpartiesModel = {
    state: $counterpartiesState,
    filtersChanged,
    filtersReset,
    sortingsChanged,
    sortingsReset,
    pageNumberChanged,
    pageSizeChanged,
    searchChanged,
    typeChanged,
};
